import {twMerge} from '../../../common/twMerge';
import {ButtonColorScheme, ButtonSize} from './Button.enum';
import {ButtonProps} from './ButtonProps.interface';

/**
 *
 * @param shape ButtonSize.Medium, Small에서만 사용 가능.
 */
export function Button({
  size = ButtonSize.Large,
  colorScheme = ButtonColorScheme['1st'],
  children,
  disabled,
  isRound = false,
  onClick,
  bottomSafeAreaPaddingNeeded,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      className={twMerge(
        sizeToStyle[size],
        bottomSafeAreaPaddingNeeded && 'bottom-button-safe-area',
        disabled
          ? colorSchemeToStyle[ButtonColorScheme.Disable]
          : colorSchemeToStyle[colorScheme],
        isRound ? 'rounded-full' : 'rounded-2pxr',
        // round on 상태의 medium 버튼만 사이즈가 다릅니다. 예외케이스가 이것 하나뿐이라 예외로 처리했고, round에 따른 스타일변화가 커지면 스타일을 분기하는 게 좋을 것 같습니다.
        isRound && size === ButtonSize.Medium && 'px-14pxr',
        // 활성화된 경우 내용물 전체에 opacity 적용
        'select-none all-child-active:opacity-30',
        // onClick이 없는 경우 클릭 이벤트를 처리할 수 없는 상태이기 때문에 커서와 클릭시 색상을 변경하지 않습니다.
        !onClick && 'cursor-default active:text-current',
        className
      )}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

const sizeToStyle = {
  // 기본은 full size로 두었지만 상황에 따라 width를 가변적으로 설정하도록 디자인가이드에 쓰여있습니다.
  [ButtonSize.Large]: 'w-full px-16pxr min-h-[48px] font-medium1-bold',
  [ButtonSize.Medium]: 'px-10pxr min-h-[32px] font-small1 min-w-44pxr',
  [ButtonSize.Small]: 'px-8pxr min-h-[22px] font-small2 min-w-37pxr',
  [ButtonSize.BottomFixed]: 'w-full px-16pxr min-h-[60px] font-medium1-bold',
};

const colorSchemeToStyle = {
  [ButtonColorScheme['1st']]:
    'bg-static-point-yellow text-static-mono-90 active:text-[rgb(var(--colors-static-mono-90)_/_30%)]',
  [ButtonColorScheme['2nd']]:
    'bg-theme-solid-100 text-theme-solid-10 active:text-[rgb(var(--colors-theme-solid-10)_/_30%)]',
  [ButtonColorScheme['3rd']]:
    'bg-theme-solid-10 text-theme-solid-100 active:text-[rgb(var(--colors-theme-solid-100)_/_30%)]',
  // 가이드상 Small에만 존재하는 scheme이지만 color scheme은 전체 버튼을 대상으로 동일하게 관리되므로, 별도 분기 없이 넣었습니다.
  [ButtonColorScheme['4st']]:
    'bg-theme-solid-20 text-theme-solid-100 active:text-[rgb(var(--colors-theme-solid-100)_/_30%)]',
  [ButtonColorScheme['sp']]: 'bg-static-point-helix text-static-mono-white',
  [ButtonColorScheme['sp_2nd']]: 'bg-theme-transparent-20 text-theme-solid-100',
  /** TODO: 디자인 시스템에 없고 PC/에러페이지에서만 사용되는 버튼 스타일입니다. 디자인 시스템에 추가될지 문의드린 후에 네이밍 변경이 있다면 수정하겠습니다.
   * https://www.figma.com/file/lun1D9CzpkoufkINaOHID1/NEXT_Design_System?node-id=3475%3A22290
   */
  [ButtonColorScheme.Text]:
    'bg-none text-theme-solid-100 active:text-[rgb(var(--colors-theme-solid-100)_/_30%)]',
  [ButtonColorScheme.Disable]:
    'cursor-default bg-theme-solid-10 text-theme-solid-20 active:text-theme-solid-20',
};
